import { Button, message, Upload, UploadProps } from 'antd';
import { Input } from 'antd';
import SearchIcon from 'assets/icons/search';
import FilterIcon from 'assets/icons/filter';
import MapIcon from 'assets/icons/map';
import { MetricCard } from 'components/features/schedule-of-values/metric-card';
import ActionButton from 'components/features/schedule-of-values/action-button';
import ExportIcon from 'assets/icons/export';
import ScheduleOfValuesTable from 'components/features/schedule-of-values/schedule-of-values-table';
import FormActionButtons from 'components/ui/form-action-buttons';
import { SubmissionTabProps } from 'routes/Submissions/SubmissionDetail/navigation-tab/submission-config-tabs';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidSOVFormat, VALID_SOV_FORMAT } from 'constants/files';
import { CustomFile } from 'components/features/file-uploader/types';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { handleUpload } from 'services/blob-storage';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  GetTailoredResultQueryDtoPriceRatingTypeEnum,
  UploadedFileResponseDtoStatusEnum,
} from 'libs/pinnacle-sdk';
import StatusLoader from 'components/features/schedule-of-values/status-loader';
import { priceRatingGatewayUrl, scheduleOfValuesApi } from 'services/apis';
import { SETTLE_SOV_FILE_STATUS } from 'components/features/schedule-of-values/constants';
import { useQuery } from 'react-query';
import usePagination from 'hooks/usePagination';
import { EQueryKeys } from 'constants/query-keys';
import { parseNumericFieldsArray } from 'components/features/schedule-of-values/schedule-of-values-table/utils';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import useSocketIO, { ESocketEvents } from 'hooks/useWebSocket';
import { IPriceRatingUpdatedMessage } from 'services/apis/types';
import { isNil } from 'lodash';
import { isSubmissionEditable } from 'utils/submission';

const ScheduleOfValues: React.FC<SubmissionTabProps> = ({
  submission,
  includeBack,
  includeNext,
  navigateBack,
  navigateNext,
}) => {
  const [searchText, setSearchText] = useState('');
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination();
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [isUploadConfirmationModalOpen, setIsUploadConfirmationModalOpen] =
    useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);

  const [fileId, setFileId] = useState<string | null>(null);
  const [fileStatus, setFileStatus] =
    useState<UploadedFileResponseDtoStatusEnum | null>(null);

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submission.status);
  }, [submission.status]);

  const isUploadButtonDisabled = useMemo(() => {
    if (!isEditingMode) {
      return true;
    }

    if (isNil(fileId)) {
      return false;
    }
    if (fileStatus && !SETTLE_SOV_FILE_STATUS.includes(fileStatus)) {
      return true;
    }
    return false;
  }, [fileId, fileStatus, isEditingMode]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!!fileStatus && !SETTLE_SOV_FILE_STATUS.includes(fileStatus)) {
        e.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [fileStatus]);

  const { t } = useTranslation();

  const handleConfirmedUpload = useCallback(
    async (targetFile?: File) => {
      if (!targetFile && !fileToUpload) return;

      const file = targetFile || (fileToUpload as File);
      setFileToUpload(null);
      setIsUploadConfirmationModalOpen(false);

      const isValidFileFormat = isValidSOVFormat(
        file.name.split('.').pop() || ''
      );
      if (!isValidFileFormat) {
        message.error(
          t('file.invalidFormat', {
            fileTypes: VALID_SOV_FORMAT.map((f) => f.toUpperCase()).join(', '),
          })
        );
        return;
      }

      const uploadFile: CustomFile = {
        originalFile: file,
        status: EFileStatus.UPLOADING,
        percent: 0,
        uid: file.name,
        size: file.size,
        name: file.name,
      };

      const fileId = await handleUpload({
        customFile: uploadFile,
        entityType: CreateUploadedFilePayloadDtoEntityTypeEnum.SubmissionSov,
        fileName: file.name,
        entityId: submission.submissionId,
      });
      setFileId(fileId);
      await scheduleOfValuesApi.processSovFile({
        fileId,
      });
      resetPagination();
    },
    [
      fileToUpload,
      setFileToUpload,
      setIsUploadConfirmationModalOpen,
      t,
      submission.submissionId,
      resetPagination,
    ]
  );

  const {
    data: sovData,
    isLoading: isLoadingScheduleOfValues,
    refetch: refetchSovData,
  } = useQuery(
    [
      EQueryKeys.FILTER_SOV,
      submission.submissionId,
      pageSize,
      currentPage,
      searchText,
      fileStatus,
    ],
    async () => {
      return scheduleOfValuesApi.filterSov({
        filterSovQueryDto: {
          submissionId: submission.submissionId,
          searchText,
        },
        page: currentPage,
        pageSize: pageSize,
      });
    },
    {
      enabled: !!submission.submissionId,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
    }
  );

  const { data: sovSummary, refetch: refetchSovSummary } = useQuery(
    [EQueryKeys.GET_SOV_SUMMARY, submission.submissionId, fileStatus],
    async () => {
      return scheduleOfValuesApi.getSovSummary({
        getSovSummaryQueryDto: {
          submissionId: submission.submissionId,
        },
      });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const props: UploadProps = {
    name: 'file',
    beforeUpload: async (file) => {
      setFileToUpload(file);
      if (sovData?.items && sovData.items.length > 0) {
        setIsUploadConfirmationModalOpen(true);
        return Upload.LIST_IGNORE;
      } else {
        return handleConfirmedUpload(file);
      }
    },
    itemRender: () => null,
  };

  const scheduleOfValues = useMemo(() => {
    return parseNumericFieldsArray(sovData?.items);
  }, [sovData]);

  const { sendMessage } = useSocketIO<IPriceRatingUpdatedMessage>({
    url: priceRatingGatewayUrl,
    requireAuth: true,
    onConnect: () => {
      sendMessage(ESocketEvents.SUBSCRIBE, {
        submissionId: submission.submissionId,
      });
    },
    registerEvents: [
      {
        event: ESocketEvents.PRICE_RATING_UPDATED,
        onMessage: async (eventMessage) => {
          const {
            data: { type, status },
            submissionId,
          } = eventMessage;

          if (
            type ===
              GetTailoredResultQueryDtoPriceRatingTypeEnum.ScheduleOfValues &&
            submissionId === submission.submissionId
          ) {
            await Promise.all([refetchSovSummary(), refetchSovData()]);
          }

          if (
            type ===
              GetTailoredResultQueryDtoPriceRatingTypeEnum.ScheduleOfValuesFileStatus &&
            submissionId === submission.submissionId &&
            status
          ) {
            setFileStatus(status);
            if (status === UploadedFileResponseDtoStatusEnum.Completed) {
              message.success(
                t('file.uploadSuccess', {
                  fileName: fileToUpload?.name || t('general.file'),
                })
              );
            } else if (status === UploadedFileResponseDtoStatusEnum.Failed) {
              message.error(t('file.unsuccessfulCheckSourceFile'));
            }
          }
        },
      },
    ],
  });

  return (
    <div className="rounded-lg px-4 bg-white py-8 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">
          {t('submission.navigation.scheduleOfValues')}
        </h1>
        <div className="flex gap-6 items-center">
          {!!fileId && fileStatus && <StatusLoader status={fileStatus} />}

          <Upload {...props}>
            <Button
              className="flex items-center gap-2 px-6 py-5 shadow-md bg-[#1d67b6] transition-colors"
              disabled={isUploadButtonDisabled}
              type="primary"
            >
              <ExportIcon
                color={isUploadButtonDisabled ? '#939393' : '#F6FCFF'}
              />
              {t('submission.uploadFile')}
            </Button>
          </Upload>
        </div>
      </div>

      {/* Metrics */}
      <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
        <MetricCard
          title={t('scheduleOfValues.totalInsurableValueCurrency', {
            currency: submission.originalCurrency || 'AUD',
          })}
          value={
            sovSummary?.totalTiv
              ? Number(sovSummary.totalTiv).toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })
              : '0'
          }
          disableAbbreviation
        />
        <MetricCard
          title={t('scheduleOfValues.incidentalTiv', {
            countries: sovSummary?.incidentalTivCountries?.join(', ') || '',
          })}
          value={sovSummary?.incidentalTiv || '0'}
        />
        <MetricCard
          title={t('scheduleOfValues.locationAccuracyPercentage')}
          value={String(sovSummary?.locationAccuracyPercentage || 0)}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex flex-wrap gap-2">
          <ActionButton
            content={t('scheduleOfValues.fetchCoordinates')}
            icon={MapIcon}
            disabled={!isEditingMode}
          />
          {/* <ActionButton
            content={t('scheduleOfValues.fetchFromCatNat')}
            icon={SwapIcon}
            disabled={!isEditingMode}
          /> */}
        </div>
        <div className="flex gap-2">
          <Input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={<SearchIcon />}
            placeholder={t('general.search')}
            size="large"
            className="w-[320px] rounded-xl shadow-md"
          />
          <button className="p-2 rounded-xl shadow-md border hover:bg-gray-50">
            <FilterIcon />
          </button>
        </div>
      </div>

      {/* Table */}
      <ScheduleOfValuesTable
        submissionId={submission.submissionId}
        submissionStatus={submission.status}
        onErrorChange={setIsNextDisabled}
        scheduleOfValues={scheduleOfValues}
        isLoadingScheduleOfValues={isLoadingScheduleOfValues}
        currentPage={currentPage}
        totalRows={sovData?.pagination.total || 0}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        fileId={sovData?.fileId}
      />

      <FormActionButtons
        confirmText={t('general.next')}
        cancelText={t('general.back')}
        isConfirmDisabled={isNextDisabled}
        includeCancel={includeBack}
        includeConfirm={includeNext}
        confirmOutlined={false}
        onCancel={navigateBack}
        onConfirm={navigateNext}
      />

      <ConfirmationModal
        isOpen={isUploadConfirmationModalOpen}
        onClose={() => {
          setFileToUpload(null);
          setIsUploadConfirmationModalOpen(false);
        }}
        onConfirm={() => handleConfirmedUpload()}
        title={t('file.confirmUpload')}
        content={t('file.confirmUploadContent', {
          fileName: fileToUpload?.name || '',
        })}
        okText={t('general.proceed')}
        cancelText={t('general.cancel')}
      />
    </div>
  );
};

export default ScheduleOfValues;
